// src/utils/simulateTransactions.ts
import axios from 'axios'

/**
 * This module no longer hard‐codes the JWT or auto‐starts on import.
 * Instead, you call `startSimulator(token: string)` and it will begin
 * posting. You can call `stopSimulator()` to end the loop.
 */

// ─── CONFIGURED VIA startSimulator() ──────────────────────────────────────
let SIMULATION_BEARER_TOKEN = ''
let isRunning = false
let nextTimeoutId: ReturnType<typeof setTimeout> | null = null

// ─── AXIOS CLIENT DOES NOT INCLUDE AUTH HEADER YET ────────────────────────
const simApi = axios.create({
  baseURL: process.env.REACT_APP_SIM_API_BASE_URL,
})

// ─── PARAMETERS FOR “fake stock‐movement” ─────────────────────────────────
const PRODUCT_IDS = [0, 1, 2, 3]
const MEAN_QUANTITY = 5
const STDDEV_QUANTITY = 1.0

/** Returns a random integer ≥ 1 from N(mean,stdDev) via Box–Muller. */
function gaussianRandomInt(mean: number, stdDev: number): number {
  let u = 0,
    v = 0
  while (u === 0) u = Math.random()
  while (v === 0) v = Math.random()

  const standardNormal =
    Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)
  const raw = standardNormal * stdDev + mean
  const rounded = Math.round(raw)
  return rounded < 1 ? 1 : rounded
}

/** Returns a random delay between minSeconds and maxSeconds. */
function randomDelayMs(minSeconds: number, maxSeconds: number): number {
  const minMs = minSeconds * 1000
  const maxMs = maxSeconds * 1000
  return Math.floor(Math.random() * (maxMs - minMs + 1) + minMs)
}

/**
 * Posts one “stock‐decrease” event and then re‐schedules itself
 * only if `isRunning` is still true.
 */
function scheduleNextTransaction(): void {
  if (!isRunning) {
    return
  }

  // 1) Pick a random product ID
  const productId =
    PRODUCT_IDS[Math.floor(Math.random() * PRODUCT_IDS.length)]

  // 2) Compute a random “quantity” via Gaussian(μ=MEAN_QUANTITY, σ=STDDEV_QUANTITY)
  const qtyMagnitude = gaussianRandomInt(MEAN_QUANTITY, STDDEV_QUANTITY)

  // 3) We only send decreases → negative:
  const signedQuantity = -qtyMagnitude

  // 4) Build the payload
  const payload = {
    product_id: productId,
    quantity: signedQuantity,
  }

  // 5) Post to `/record_stock_movement` with the current JWT
  simApi
    .post(
      '/record_stock_movement',
      payload,
      {
        headers: {
          Authorization: `Bearer ${SIMULATION_BEARER_TOKEN}`,
          'Content-Type': 'application/json',
        },
      }
    )
    .then((response) => {
      console.log(
        `[Simulator] Posted [product_id=${productId}, quantity=${signedQuantity}] → status ${response.status}`
      )
    })
    .catch((err) => {
      console.error(
        `[Simulator] ERROR posting [product_id=${productId}, quantity=${signedQuantity}]:`,
        err
      )
      // If you want to bail out entirely on a 401 (or any error), you could do:
      // stopSimulator()
    })
    .finally(() => {
      // 6) Only schedule the next one if still running
      if (isRunning) {
        const delay = randomDelayMs(1, 5)
        nextTimeoutId = setTimeout(scheduleNextTransaction, delay)
      }
    })
}

/**
 * Call this to start the infinite simulation loop.
 * You must supply a non‐empty JWT. If already running, just updates the token.
 */
export function startSimulator(token: string): void {
  SIMULATION_BEARER_TOKEN = token
  if (isRunning) {
    // Already running; next calls will use the updated token.
    return
  }
  isRunning = true
  // Kick off the first call after a short random initial delay (1–5s)
  const initialDelay = randomDelayMs(1, 5)
  nextTimeoutId = setTimeout(scheduleNextTransaction, initialDelay)
}

/** Stops the loop (no further posts will be scheduled). */
export function stopSimulator(): void {
  isRunning = false
  if (nextTimeoutId !== null) {
    clearTimeout(nextTimeoutId)
    nextTimeoutId = null
  }
}

/** Returns whether the simulator is currently running. */
export function isSimulatorRunning(): boolean {
  return isRunning
}
